/**
 * GA4 Tracking Block
 *
 * Injection block that loads Google Analytics 4 (gtag.js) with a
 * per-property measurement ID and forwards canonical Vibesite events from
 * the runtime event bus into gtag.
 *
 * Multi-instance safe: a corporate site can place this block both at the
 * company scope (master measurement ID) and the property scope (per-property
 * measurement ID); each instance subscribes independently and routes its
 * call through gtag's `send_to` parameter.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockToken,
  useVibesiteEvents,
} from '@vibesites/runtime/react';
import { Event, type VibesiteEventName } from '@vibesites/runtime';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'ga4-tracking-script';

// ============================================================================
// Event mapping — canonical Vibesite event → GA4 event name + params
// ============================================================================

interface GA4EventMapping {
  /** GA4 event name (https://developers.google.com/tag-platform/gtagjs/reference/events). */
  name: string;
  /** Map the bus payload into GA4 event params (key/value pairs sent to gtag). */
  params?: (payload: Record<string, unknown>) => Record<string, unknown>;
}

/**
 * EVENT_MAP is exported so the editor's "Tracked Events" panel can render it.
 */
export const GA4_EVENT_MAP: Partial<Record<VibesiteEventName, GA4EventMapping>> = {
  [Event.contact_form_submitted]: {
    name: 'generate_lead',
    params: (p) => ({
      form_id: p.formId,
      lead_id: p.leadId,
    }),
  },
  [Event.tour_scheduled]: {
    name: 'schedule',
    params: (p) => ({
      tour_type: p.tourType,
      appointment_date: p.appointmentDate,
    }),
  },
  [Event.apply_now_clicked]: {
    name: Event.apply_now_clicked,
    params: (p) => ({
      link_url: p.href,
      link_text: p.linkText,
    }),
  },
  [Event.schedule_tour_clicked]: {
    name: Event.schedule_tour_clicked,
    params: (p) => ({
      link_url: p.href,
      link_text: p.linkText,
    }),
  },
  [Event.self_guided_tour_clicked]: {
    name: Event.self_guided_tour_clicked,
    params: (p) => ({
      link_url: p.href,
      link_text: p.linkText,
    }),
  },
  [Event.virtual_tour_clicked]: {
    name: Event.virtual_tour_clicked,
    params: (p) => ({
      link_url: p.href,
      link_text: p.linkText,
    }),
  },
  [Event.resident_portal_clicked]: {
    name: Event.resident_portal_clicked,
    params: (p) => ({
      link_url: p.href,
    }),
  },
  [Event.phone_clicked]: {
    name: 'phone_call',
    params: (p) => ({
      phone_number: p.phoneNumber,
    }),
  },
  [Event.email_clicked]: {
    name: 'email_click',
    params: (p) => ({
      email_address: p.emailAddress,
    }),
  },
};

function GA4TrackingComponent(_props: BlockProps) {
  const measurementId = useBlockToken('measurement_id', {
    defaultField: 'custom_ga4_id',
  });
  const { subscribe } = useVibesiteEvents();
  const trackingAllowed = useTrackingAllowed('analytics');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !measurementId) return;

    const w = window as unknown as {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
      __ga4_configured_ids?: Set<string>;
    };

    // Load gtag.js and initialize the dataLayer + gtag() shim once per page.
    // gtag.js supports multiple configured measurement IDs via repeated
    // gtag('config', ...) calls, so the library only needs to load once.
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      const inline = document.createElement('script');
      inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;gtag('js',new Date());`;
      document.head.appendChild(inline);
    }

    // Configure this instance's measurement ID exactly once per page. Multiple
    // ga4-tracking block instances (e.g. corporate + per-property) can coexist
    // on the same page, each contributing its own measurement ID.
    const configured = (w.__ga4_configured_ids ||= new Set<string>());
    if (!configured.has(measurementId) && typeof w.gtag === 'function') {
      w.gtag('config', measurementId);
      configured.add(measurementId);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, measurementId]);

  // Subscribe to canonical events and forward to gtag scoped to THIS
  // measurement ID via `send_to`. The bus + DOM capture is mounted by
  // VibesitesProvider; we just attach a listener.
  useEffect(() => {
    if (!trackingAllowed || !measurementId) return;

    const off = subscribe('*', (name, payload) => {
      console.log('GA4TrackingComponent: name', name);
      const mapping = GA4_EVENT_MAP[name];
      if (!mapping) return;

      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (typeof w.gtag !== 'function') return;

      const params = mapping.params
        ? mapping.params(payload as unknown as Record<string, unknown>)
        : {};
      // Drop undefined params so gtag's payload stays clean.
      const cleanParams: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined) cleanParams[k] = v;
      }

      w.gtag('event', mapping.name, {
        send_to: measurementId,
        ...cleanParams,
      });
    });

    return off;
  }, [trackingAllowed, measurementId, subscribe]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'GA4Tracking',
  displayName: 'GA4 Tracking',
  tokens: {
    measurement_id: {
      label: 'Measurement ID',
      type: 'text',
      defaultField: 'custom_ga4_id',
      suggestedFields: ['custom_ga4_id'],
      description: 'GA4 measurement ID (e.g. G-XXXXXXXXXX). Per-property.',
      required: true,
    },
  },
};

export const ga4TrackingBlock: BlockDefinition = {
  id: 'ga4-tracking',
  name: 'GA4 Tracking',
  description:
    'Google Analytics 4 tracking. Loads gtag.js and forwards Vibesite canonical events to the configured measurement ID.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'template',
  component: GA4TrackingComponent,
  contentSchema,
  tags: ['analytics', 'ga4', 'google', 'tracking', 'measurement'],
  trackedEvents: (Object.keys(GA4_EVENT_MAP) as Array<keyof typeof GA4_EVENT_MAP>)
    .filter((name): name is VibesiteEventName => !!GA4_EVENT_MAP[name])
    .map((name) => ({
      event: name as VibesiteEventName,
      mapsTo: `gtag('event', '${GA4_EVENT_MAP[name]!.name}', { send_to })`,
    })),
};
